"use client";

import { useEffect } from "react";
import Link from "next/link";
import { trackEvent } from "@/lib/analytics";

export default function SignupError({
  error,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    try {
      trackEvent("signup_crash", {
        error_name: error.name || "Error",
        has_digest: Boolean(error.digest),
      });
    } catch {
      // Never let analytics suppress the error boundary.
    }
  }, [error]);

  return (
    <div className="max-w-[520px] mx-auto px-5 py-20 text-center animate-fade-in">
      <div className="w-16 h-16 rounded-full bg-error/10 flex items-center justify-center mx-auto mb-5">
        <svg
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#EF4444"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      </div>

      <h2 className="font-serif text-2xl text-navy mb-3">
        Something went wrong
      </h2>
      <p className="text-[14px] text-text-muted mb-6 leading-relaxed">
        The signup page ran into an issue. Try again, or clear your browser
        data for this site and refresh.
      </p>

      <div className="flex gap-3 justify-center flex-wrap mb-8">
        <button
          onClick={() => {
            try {
              sessionStorage.removeItem("pw_signup_progress");
              localStorage.removeItem("pw_signup_draft");
            } catch {
              /* ignore */
            }
            // Full page reload bypasses any broken React/router state
            window.location.href = "/signup";
          }}
          className="px-6 py-3 rounded-sm text-[14px] font-semibold bg-gold text-white border-none cursor-pointer font-sans transition-all hover:bg-gold-light"
        >
          Try Again
        </button>
        <Link
          href="/"
          className="px-6 py-3 rounded-sm text-[14px] font-semibold text-text-muted border-2 border-border no-underline font-sans transition-all hover:border-charcoal hover:text-charcoal"
        >
          Go Home
        </Link>
      </div>

      <details className="text-left bg-warm-bg rounded-md p-4 text-[12px] text-text-muted">
        <summary className="cursor-pointer font-medium mb-2">
          Error details
        </summary>
        <code className="block whitespace-pre-wrap break-all">
          {error.message}
          {error.digest && `\nDigest: ${error.digest}`}
          {error.stack && `\n\nStack:\n${error.stack.slice(0, 1000)}`}
        </code>
      </details>
    </div>
  );
}
